/* Web Push helpers: opt-in, subscribe, unsubscribe.
 * Alle helpers zijn defensief — bij onbekende error geeft de helper false/null
 * terug in plaats van te crashen. UI toont errors indien nuttig.
 */
import api from "@/lib/api";

/** True als de browser Web Push voldoende ondersteunt. */
export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/** Current permission: "default" | "granted" | "denied". */
export function pushPermission() {
  if (!isPushSupported()) return "default";
  return Notification.permission;
}

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}

/** Vraag permissie én registreer subscription. Retourneert {ok, reason}. */
export async function enablePush() {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  const perm = await Notification.requestPermission();
  if (perm !== "granted") return { ok: false, reason: "denied" };
  try {
    const reg = await navigator.serviceWorker.ready;
    // Bestaande subscription hergebruiken indien mogelijk
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      const { data } = await api.get("/push/vapid-public");
      const applicationServerKey = urlBase64ToUint8Array(data.public_key);
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey,
      });
    }
    const payload = sub.toJSON();
    await api.post("/push/subscribe", {
      ...payload,
      user_agent: navigator.userAgent,
    });
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || "subscribe-failed" };
  }
}

/** Uitschakelen: unsubscribe browser-side + verwijder op server. */
export async function disablePush() {
  if (!isPushSupported()) return { ok: true };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      const endpoint = sub.endpoint;
      await sub.unsubscribe();
      await api.post("/push/unsubscribe", { endpoint });
    } else {
      // Server-side cleanup (alle subs) — voor het geval de browser leeg is maar server niet
      await api.post("/push/unsubscribe", {});
    }
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: e?.message || "unsubscribe-failed" };
  }
}

/** True als een geldige subscription bestaat in de browser. */
export async function hasActiveSubscription() {
  if (!isPushSupported()) return false;
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    return !!sub;
  } catch (_) {
    return false;
  }
}

/** Test-push (via backend). */
export async function sendTestPush() {
  try {
    const { data } = await api.post("/push/test");
    return { ok: true, ...data };
  } catch (e) {
    return { ok: false, reason: e?.response?.data?.detail || e?.message };
  }
}

/** GET/PUT push-preferences. */
export async function getPushPreferences() {
  try {
    const { data } = await api.get("/push/preferences");
    return data;
  } catch (_) {
    return { level: "all", quiet_hours: null };
  }
}
export async function setPushPreferences(prefs) {
  const { data } = await api.put("/push/preferences", prefs);
  return data;
}
